import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BASE_URL } from '../../../shared/constants';
import OrderItemsMobile from '../orderConfirmation/OrderItemsMobile';
import OrderDeliveryMobile from '../orderConfirmation/OrderDeliveryMobile';
import OrderStatusBadge from '../../shared/OrderStatusBadge';

const OrderDetailsMobile = ({ order }) => {
  const [modalImage, setModalImage] = useState('');

  if (!order) return null;

  // Визначення стану кожного етапу та його лейбла
  const getTimelineSteps = () => {
    const currentStatus = order.orderStatus;
    
    // Перший етап - завжди "Очікує на обробку"
    // Синій тільки якщо статус "Очікує на обробку", інакше зелений (виконано)
    const step1 = {
      label: 'Очікує на обробку',
      status: currentStatus === 'Очікує на обробку' ? 'active' : 'completed'
    };
    
    // Другий етап - може бути різним
    let step2 = null;
    if (currentStatus === 'Підтверджено, очікує на пакування') {
      // Якщо статус "Підтверджено", то воно вже виконано - зелений
      step2 = {
        label: 'Підтверджено, очікує на пакування',
        status: 'completed'
      };
    } else if (currentStatus === 'Відкладено') {
      // Відкладено - жовтий (special pending)
      step2 = {
        label: 'Відкладено',
        status: 'special-pending'
      };
    } else if (currentStatus === 'Передзамовлення') {
      // Передзамовлення - жовтий (special pending)
      step2 = {
        label: 'Передзамовлення',
        status: 'special-pending'
      };
    } else if (currentStatus === 'Упаковано, очікує на відправлення' || 
               currentStatus === 'Відправлено') {
      // Якщо вже пройшли далі, другий крок був "Підтверджено, очікує на пакування" - зелений
      step2 = {
        label: 'Підтверджено, очікує на пакування',
        status: 'completed'
      };
    }
    
    // Третій етап - "Упаковано, очікує на відправлення"
    // Якщо статус "Упаковано" або "Відправлено", то вже виконано - зелений
    const step3 = {
      label: 'Упаковано, очікує на відправлення',
      status: currentStatus === 'Упаковано, очікує на відправлення' ? 'completed' :
              currentStatus === 'Відправлено' ? 'completed' : 'pending'
    };
    
    // Четвертий етап - "Відправлено"
    // Якщо статус "Відправлено", то виконано - зелений
    const step4 = {
      label: 'Відправлено',
      status: currentStatus === 'Відправлено' ? 'completed' : 'pending'
    };
    
    // Якщо статус відмінено або повернено - показуємо як спеціальний
    if (currentStatus === 'Відмінено' || currentStatus === 'Повернено') {
      return null; // Повертаємо null для спеціальної обробки
    }
    
    return [step1, step2, step3, step4].filter(step => step !== null);
  };

  const timelineSteps = getTimelineSteps();
  const isCancelledOrReturned = order.orderStatus === 'Відмінено' || order.orderStatus === 'Повернено';

  // Функції для отримання кольорів та іконок
  const getStatusColor = (stepStatus) => {
    if (stepStatus === 'completed') {
      return '#27AE60'; // Зелений для виконаних етапів
    } else if (stepStatus === 'active') {
      return '#2F80ED'; // Синій тільки для "Очікує на обробку"
    } else if (stepStatus === 'special-pending') {
      return '#F2C94C'; // Жовтий для спеціальних статусів (Передзамовлення, Відкладено)
    } else {
      return '#E0E0E0'; // Сірий для майбутніх етапів (pending)
    }
  };

  const getStatusIcon = (stepStatus) => {
    if (stepStatus === 'completed') {
      return 'bi-check-circle-fill';
    } else if (stepStatus === 'active') {
      return 'bi-clock-fill';
    } else if (stepStatus === 'special-pending') {
      return 'bi-clock-fill';
    } else {
      return 'bi-circle';
    }
  };

  // Для відмінених/повернених статусів
  const getSpecialStatusColor = () => {
    if (order.orderStatus === 'Відмінено' || order.orderStatus === 'Повернено') {
      return '#EB5757';
    }
    return '#6C757D';
  };

  const getSpecialStatusIcon = () => {
    if (order.orderStatus === 'Відмінено') return 'bi-x-circle-fill';
    if (order.orderStatus === 'Повернено') return 'bi-arrow-counterclockwise';
    return 'bi-circle';
  };

  return (
    <div className='order-details-content pt-3'>
      {/* ===== HEADER ===== */}
      <div className='mb-3 pl-1'>
        <h5 className='font-weight-bold mb-0 text-center'>
          Замовлення №{order.orderId}
        </h5>
        {order.createTime && (
          <div className='text-muted mb-3 text-center' style={{ fontSize: '0.875rem' }}>
            Дата створення: {order.createTime}
          </div>
        )}
        <div className='text-muted mb-1' style={{ fontSize: '0.875rem' }}>
          Статус: <OrderStatusBadge orderStatus={order.orderStatus} />
        </div>
        <div className='text-muted' style={{ fontSize: '0.875rem' }}>
          Статус оплати:{' '}
          <span>{order.paymentType}</span>
          {order.paymentType !== "Оплата при отриманні" && (
            <span className="ml-2">
              {order.paymentStatus === "Оплачений" ? (
                <strong style={{ color: "#27AE60", fontSize: "0.95rem" }}>
                  <i className="bi bi-check-circle-fill mr-1" style={{ color: "#27AE60", fontSize: "0.95rem" }}></i>
                  {order.paymentStatus}
                </strong>
              ) : (
                <strong style={{ color: "#E0A800", fontSize: "0.95rem" }}>
                  <i className="bi bi-clock mr-1" style={{ color: "#E0A800", fontSize: "0.95rem" }}></i>
                  {order.paymentStatus}
                </strong>
              )}
            </span>
          )}
        </div>
      </div>

      {/* ===== STATUS TIMELINE ===== */}
      <div className='card mb-3'>
        <h5 className='card-header font-weight-bold p-2'>
          <i className='bi bi-list-check mr-2'></i>
          Статус замовлення
        </h5>
        <div className='card-body p-2'>
          {isCancelledOrReturned ? (
            // Показуємо спеціальний статус окремо
            <div className='d-flex justify-content-center align-items-center'>
              <div className='d-flex flex-column align-items-center'>
                <div
                  className='rounded-circle d-flex align-items-center justify-content-center'
                  style={{
                    width: '40px',
                    height: '40px',
                    backgroundColor: getSpecialStatusColor(),
                    color: '#fff',
                    fontSize: '1rem',
                    marginBottom: '0.5rem'
                  }}
                >
                  <i className={`bi ${getSpecialStatusIcon()}`}></i>
                </div>
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: '#000',
                    fontWeight: 'bold'
                  }}
                >
                  {order.orderStatus}
                </div>
              </div>
            </div>
          ) : timelineSteps && timelineSteps.length > 0 ? (
            // Показуємо флоу доставки
            <div className='d-flex flex-column'>
              {timelineSteps.map((step, index) => {
                const color = getStatusColor(step.status);
                const icon = getStatusIcon(step.status);
                
                return (
                  <React.Fragment key={index}>
                    <div className='d-flex align-items-center mb-3'>
                      <div
                        className='rounded-circle d-flex align-items-center justify-content-center'
                        style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: color,
                          color: step.status === 'pending' ? '#999' : '#fff',
                          fontSize: '1rem',
                          flexShrink: 0,
                          marginRight: '1rem',
                          border: step.status === 'pending' ? '2px solid #E0E0E0' : 'none'
                        }}
                      >
                        <i className={`bi ${icon}`}></i>
                      </div>
                      <div
                        style={{
                          fontSize: '0.875rem',
                          color: step.status === 'active' ? '#000' : step.status === 'completed' ? '#27AE60' : step.status === 'special-pending' ? '#F2994A' : '#6C757D',
                          fontWeight: step.status === 'active' ? 'bold' : 'normal',
                          flex: 1
                        }}
                      >
                        {step.label}
                      </div>
                    </div>
                    {index < timelineSteps.length - 1 && (
                      <div
                        style={{
                          width: '2px',
                          height: '20px',
                          backgroundColor: step.status === 'completed' ? '#27AE60' : step.status === 'active' ? '#2F80ED' : step.status === 'special-pending' ? '#F2C94C' : '#E0E0E0',
                          marginLeft: '19px',
                          marginBottom: '0.5rem'
                        }}
                      ></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {/* ===== ITEMS ===== */}
      <OrderItemsMobile
        order={order}
        items={order.items}
        totalPrice={order.totalPrice}
        discountedPrice={order.discountedPrice}
        setModalImage={setModalImage}
      />

      {/* ===== DELIVERY ===== */}
      <OrderDeliveryMobile delivery={order.delivery} />

      {/* ===== USER COMMENT ===== */}
      {order.userComment && (
        <div className='card mb-3'>
          <h5 className='card-header font-weight-bold p-2'>
            💬 Ваш коментар до замовлення
          </h5>
          <div className='card-body'>
            <div style={{ whiteSpace: 'pre-line' }}>{order.userComment}</div>
          </div>
        </div>
      )}

      {/* ===== ADMIN COMMENT ===== */}
      {order.adminComment && (
        <div className='card mb-3'>
          <h5 className='card-header font-weight-bold p-2'>
            📝 Коментар адміністратора
          </h5>
          <div className='card-body'>
            <div style={{ whiteSpace: 'pre-line' }}>{order.adminComment}</div>
          </div>
        </div>
      )}

      {/* ===== TRACKING NUMBER ===== */}
      {order.trackingNumber && (
        <div className='card mb-3'>
          <h5 className='card-header font-weight-bold p-2'>
            📦 Трекінг-номер
          </h5>
          <div className='card-body'>
            <div
              onClick={() => window.open(`https://novaposhta.ua/tracking/${order.trackingNumber}`, '_blank')}
              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <strong style={{ fontSize: '1rem' }}>
                {order.trackingNumber}
              </strong>
              <i className='bi bi-box-arrow-up-right'></i>
            </div>
            <div className='text-muted mt-2' style={{ fontSize: '0.8rem' }}>
              Натисніть для відстеження на сайті Нової Пошти
            </div>
          </div>
        </div>
      )}

      {/* ===== CONTACT INFO ===== */}
      <div className='text-center text-muted mb-3 px-3'>
        <p className='mb-0'>
          З будь-яких питань -{' '}
          <a
            href='https://t.me/waaaghShopAdmin'
            target='_blank'
            rel='noopener noreferrer'
            className='font-weight-bold'
          >
            звертайтеся до нас у Telegram
          </a>{' '}
          або телефонуйте за номером, вказаним у шапці сайту
        </p>
      </div>

      {/* ===== ACTIONS ===== */}
      <div className='text-center pb-3'>
        <Link to='/user/personal-cabinet' className='btn btn-dark mr-2'>
          Мої замовлення
        </Link>
        <Link to='/' className='btn btn-success'>
          На головну
        </Link>
      </div>

      {/* ===== MODAL FOR IMAGE ===== */}
      <div
        className='modal fade'
        id='imageModalMobile'
        tabIndex='-1'
        role='dialog'
        aria-hidden='true'
      >
        <div className='modal-dialog modal-dialog-centered modal-lg' role='document'>
          <div className='modal-content bg-transparent border-0'>
            <div className='modal-body p-0 text-center'>
              <img
                src={`${BASE_URL}/api/v1/product/image/${modalImage}`}
                alt='Product'
                className='img-fluid'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsMobile;